import {
	Alert,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
	TextField,
	Snackbar,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../../redux/api";
import { useDispatch } from "react-redux";
import { debounce } from "lodash";
import SmartPasswordField from "../../../commonComponents/SmartPasswordField";
import { MoreHorizOutlined } from "@mui/icons-material";
import WorkflowSessionModal from "../../ItemProperties/WorkflowSessionModal";
import { decryptString, decryption, encryptString, encryption } from "../../../utils/utilFunctions";
import _ from 'lodash';

const Database = (props) => {
	const dispatch = useDispatch();
	const {
		connectionDetails,
		handleBodyContent,
		open,
		close,
		account,
		direction,
        screen,
        useConnection,
        repoPDC,
		disableControl,
		source = "",
	} = props;
	const shouldEncryptDecryptPassword = String(source || "").toLowerCase() !== "pdc";
	// console.log(repoPDC);
	const { t } = useTranslation();
	const [dbTypesOptions, setDbTypesOptions] = useState([]);
	const [dbTypeDetails, setDbTypeDetails] = useState([]);
	const [databaseType, setDatabaseType] = useState(null);
	const [jdbcUrl, setJdbcUrl] = useState(null);
	const [username, setUsername] = useState(null);
	const [password, setPassword] = useState(null);
	const [content, setContent] = useState(null);
	const [error, setError] = useState("");
	const [showAlert, setShowAlert] = useState({
		mssg: "",
		type: "success",
		isOpen: false,
	});

	const [radioValue, setRadioValue] = useState("AUTO_COMMIT");
	const [concurrentConnection, setConcurrentConnection] = useState(0);
    const [workflowSessionModal1, setWorkflowSessionModal1] = useState({open: false, isFirstTime: false});
    const [workflowSessionModal2, setWorkflowSessionModal2] = useState({open: false, isFirstTime: false});
    const [workflowSessionModal3, setWorkflowSessionModal3] = useState({open: false, isFirstTime: false});


    const getFontWeight = (control, currentValue, connectionDetails,useConnection) => {
		if (control === "DB_WRITE_THREADS" && currentValue === 1 && !repoPDC?.hasOwnProperty(control)) return "normal";
		// console.log(repoPDC, control, currentValue);
        return ((repoPDC?.[control] !== currentValue) &&
          useConnection === "preDefined")
        ? "bold"
        : "normal";
    };
    const fontWeights = useMemo(() => {
        return {
          JDBCURL: getFontWeight("JDBCURL", jdbcUrl, connectionDetails, useConnection),
          USERNAME: getFontWeight("USERNAME", username, connectionDetails, useConnection),
          PASSWORD: getFontWeight("PASSWORD", password, connectionDetails, useConnection),
		  DB_WRITE_THREADS: getFontWeight("DB_WRITE_THREADS", concurrentConnection, connectionDetails, useConnection),
          // Add more fields as needed
        };
      }, [jdbcUrl, username, password, connectionDetails, useConnection, repoPDC, concurrentConnection]);

	const databaseTypeDropDownItems = dbTypesOptions.map((obj, index) => {
		return (
			<MenuItem key={index} value={obj}>
				{obj}
			</MenuItem>
		);
	});

	const debouncedContentUpdate = debounce(handleBodyContent, 200);

	const onInputChange = (name, value) => {	
		if(screen === 'GET_DATA' || screen === 'SEND_DATA'){
			if (name === "DATABASE_TYPE") { setContent({add: {DATABASE_TYPE: value, JDBCURL: dbTypeDetails[value].DEFAULT_URL}}) }
            else if (name === "PASSWORD") {
				setContent({[name]: shouldEncryptDecryptPassword ? encryptString(value, process.env.REACT_APP_SECRET_KEY2) : value})
			}
			else setContent({[name]:value})
        }
        else setContent((prev) => {
            return { ...prev, [name]: value };
        });
	};

	const setDefaultValue = (options, type) => {
		const option = options[type] || null;
		setJdbcUrl(option?.DEFAULT_URL || "");
		if(screen !== 'GET_DATA' && screen !== 'SEND_DATA'){
			onInputChange("JDBCURL", option?.DEFAULT_URL || "");
		}
	};

	const handleChange = (e) => {
		const value = e.target.value;
		const numericValue = Number(value);
		if (!/^\d*$/.test(value) || numericValue < 0 || numericValue > 99) {
			setError("Value must be a number between 1 and 99");
		} else {
			setError("");
			setConcurrentConnection(numericValue);
			onInputChange("DB_WRITE_THREADS", numericValue.toString());
		}
	};

	const handleBlur = () => {
		if (concurrentConnection === 0 || concurrentConnection === null || concurrentConnection === undefined || concurrentConnection === "") {
			setConcurrentConnection(1);
			onInputChange("DB_WRITE_THREADS", "1");
		}
	};

	const handleAlertClose = () => {
		setShowAlert({ ...showAlert, isOpen: false });
	};

	const removeUndefinedAndNullKeys = (obj = {}) => {
		const tempObj = {...obj}
		Object.keys(tempObj || {}).forEach(e => {
			if(tempObj[e] === undefined || tempObj[e] === null) delete tempObj[e];
		})
		return tempObj
	}

	useEffect(() => {
		setDatabaseType(connectionDetails?.DATABASE_TYPE || "");
		setJdbcUrl(connectionDetails?.JDBCURL || "");
		setUsername(connectionDetails?.USERNAME || "");
		if(screen === "GET_DATA" || screen === "SEND_DATA") {
			setPassword(
				shouldEncryptDecryptPassword
					? (decryptString((connectionDetails?.PASSWORD || ""),process.env.REACT_APP_SECRET_KEY2)) || ""
					: (connectionDetails?.PASSWORD || "")
			);
		}else {
			setPassword(connectionDetails?.PASSWORD || '');
		}
		if (connectionDetails?.TRANSACTION_TYPE === "SINGLE")
			setRadioValue("SINGLE");
		else if (connectionDetails?.TRANSACTION_TYPE === "SINGLE_NO_ERR")
			setRadioValue("SINGLE_NO_ERR");
		else setRadioValue("AUTO_COMMIT");
		setConcurrentConnection(parseInt(connectionDetails?.DB_WRITE_THREADS) || 1);
		setContent({...connectionDetails});
	}, [connectionDetails]);

	useEffect(() => {
		dispatch(api.endpoints.getDatabaseDriverList.initiate()).then((res) => {
			if (res?.data?.current_state === "SUCCESS") {
				setDbTypesOptions(Object.keys(res.data.data));
				setDbTypeDetails(res.data.data);
			}
			if (res?.data?.current_state === "ERROR") {
				setShowAlert({ mssg: res?.data?.message, isOpen: true, type: "error" });
			}
		});
	}, []);

	useEffect(() => {
		if(!_.isEqual(connectionDetails, content)) debouncedContentUpdate(content);
	}, [content]);

	return (
		<>
			<FormControl variant='outlined' className='width-100' size='small'>
				<InputLabel id='databaseTypeDropdownLabel'>
					{t("LBL_DB_TYPE")}
				</InputLabel>
				<Select
					disabled={disableControl || false}
					className={"width-100"}
					MenuProps={{
						PaperProps: {
							sx: {
								maxHeight: 300,
							},
						},
					}}
					name={"DATABASE_TYPE"}
					labelId='databaseTypeDropdownLabel'
					label={t("LBL_DB_TYPE")}
					id='databaseTypeDropdown'
					value={databaseType || ""}
					onChange={(e) => {
						onInputChange(e.target.name, e.target.value);
						setDatabaseType(e.target.value);
						setDefaultValue(dbTypeDetails, e.target.value);
					}}
				>
					{databaseTypeDropDownItems}
				</Select>
			</FormControl>
            <div style={{display:'flex', gap:'4px'}}>
                <FormControl className='width-100' size='small'>
                    <TextField
						disabled={disableControl || false}
                        variant='outlined'
                        size='small'
                        label={t("LBL_JDBC_URL")}
                        type='text'
                        value={jdbcUrl || ""}
                        sx={{ input: { fontWeight: fontWeights.JDBCURL } }}
                        onChange={(e) => {
                            onInputChange(e.target.name, e.target.value);
                            setJdbcUrl(e.target.value);
                        }}
                        className={"width-100"}
                        name={"JDBCURL"}
                        autoComplete='off'
                    />
                </FormControl>
                {(screen && screen === "GET_DATA" || screen === "SEND_DATA") && (
                    <Button
						disabled={disableControl || false}
                        size='small'
                        // disabled={disableControl || false}
                        variant='outlined'
                        onClick={() =>
                            setWorkflowSessionModal1({ open: true, isFirstTime: true })
                        }
                        sx={{ width: "40px", minWidth: "40px" }}
                    >
                        <MoreHorizOutlined fontSize='small' />
                    </Button>
                )}
            </div>
            <div style={{display:'flex', gap:'4px'}}>
                <FormControl className='width-100' size='small'>
                    <TextField
						disabled={disableControl || false}
                        variant='outlined'
                        size='small'
                        label={t("LBL_USERNAME")}
                        type='text'
                        value={username || ""}
                        sx={{ input: { fontWeight: fontWeights.USERNAME } }}
                        onChange={(e) => {
                            onInputChange("USERNAME", e.target.value);
                            setUsername(e.target.value);
                        }}
                        className={"width-100"}
                        name={"USER_NAME"}
                        autoComplete='off'
                    />
                </FormControl>
                {(screen && screen === "GET_DATA" || screen === "SEND_DATA") && (
                    <Button
						disabled={disableControl || false}
                        size='small'
                        // disabled={disableControl || false}
                        variant='outlined'
                        onClick={() =>
                            setWorkflowSessionModal2({ open: true, isFirstTime: true })
                        }
                        sx={{ width: "40px", minWidth: "40px" }}
                    >
                        <MoreHorizOutlined fontSize='small' />
                    </Button>
                )}
            </div>
            <div style={{display:'flex', gap:'4px'}}>
                <SmartPasswordField
					disabled={disableControl || false}	
                    variant='outlined'
                    size='small'
                    label={t("LBL_WS_PASSWORD")}
                    value={password || ""}
                    onChange={(e) => {
                        onInputChange("PASSWORD", e.target.value);
                        setPassword(e.target.value);
                    }}
                    className={"width-100"}
                    name={"PASS_WORD"}
                    inputProps={{
                        style: { fontWeight: fontWeights.PASSWORD }
                    }}
                />
                {(screen && screen === "GET_DATA" || screen === "SEND_DATA") && (
                    <Button
						disabled={disableControl || false}
                        size='small'
                        // disabled={disableControl || false}
                        variant='outlined'
                        onClick={() =>
                            setWorkflowSessionModal3({ open: true, isFirstTime: true })
                        }
                        sx={{ width: "40px", minWidth: "40px" }}
                    >
                        <MoreHorizOutlined fontSize='small' />
                    </Button>
                )}
            </div>
			{direction === "OUT" && (
				<>
					<div className='flex'>
						<FormControl>
							<RadioGroup
								disabled={disableControl || false}
								row
								aria-labelledby='demo-row-radio-buttons-group-label'
								name={"TRANSACTION_TYPE"}
								value={radioValue}
								onChange={(e) => {
									if (e.target.value === "AUTO_COMMIT")
										onInputChange("TRANSACTION_TYPE", "AUTO_COMMIT");
									else if (e.target.value === "SINGLE")
										onInputChange("TRANSACTION_TYPE", "SINGLE");
									else if (e.target.value === "SINGLE_NO_ERR")
										onInputChange("TRANSACTION_TYPE", "SINGLE_NO_ERR");
									else onInputChange("TRANSACTION_TYPE", "");
									setRadioValue(e.target.value);
								}}
								className='flex justify-center'
							>
								<FormControlLabel
									disabled={disableControl || false}
									value='AUTO_COMMIT'
									control={<Radio />}
									label={t("AUTO_COMMIT")}
								/>
								<FormControlLabel
									disabled={disableControl || false}
									value='SINGLE'
									control={<Radio />}
									label={t("LBL_COMMIT_REGARLESS_OF_ERRORS")}
								/>
								<FormControlLabel
									disabled={disableControl || false}
									value='SINGLE_NO_ERR'
									control={<Radio />}
									label={t("LBL_COMMIT_NO_ERRORS")}
								/>
							</RadioGroup>
						</FormControl>
					</div>
					<FormControl className='width-100' size='small'>
						<TextField
							disabled={disableControl || false}
							variant='outlined'
							size='small'
							label={t("LBL_MAX_DB_CONNECTIONS")}
							type='number'
							value={concurrentConnection}
							onChange={(e) => {
								handleChange(e);
							}}
							onBlur={handleBlur}
							className={"width-100"}
							name='DB_WRITE_THREADS'
							autoComplete='off'
							error={!!error}
							helperText={error}
							inputProps={{
								min: 1,
								max: 99,
							}}
							sx={{ input: { fontWeight: fontWeights.DB_WRITE_THREADS } }}
						/>
					</FormControl>
				</>
			)}
			<TestConnection
				jdbcUrl={jdbcUrl}
				direction={direction}
				open={open}
				close={close}
				account={account}
				username={username}
				password={password}
				type={databaseType}
				transaction_type={radioValue}
				db_threads={concurrentConnection}
			/>
			<Snackbar open={showAlert.isOpen} onClose={() => handleAlertClose()}>
				<Alert
					onClose={() => handleAlertClose()}
					severity={showAlert.type}
					variant='filled'
					sx={{ width: "100%" }}
				>
					{showAlert.mssg}
				</Alert>
			</Snackbar>
            <WorkflowSessionModal
                valueLabel={t("LBL_JDBC_URL")}
                showAccountData={true}
                showCallingWorkflow={true}
                showDateAndTime={true} 
                showSystemData={true}
                showWorkflowSession={true}
                openModal={workflowSessionModal1} 
                setModalOpen={setWorkflowSessionModal1}
                handleClose={()=>setWorkflowSessionModal1({open: false, isFirstTime: false})}
                initialValue={jdbcUrl}
                valuesHandler={(value) => {
                    onInputChange("JDBCURL", value)
                    setJdbcUrl(value)
                }}
            />
            <WorkflowSessionModal
                valueLabel={t("LBL_USERNAME")}
                showAccountData={true}
                showCallingWorkflow={true}
                showDateAndTime={true} 
                showSystemData={true}
                showWorkflowSession={true}
                openModal={workflowSessionModal2} 
                setModalOpen={setWorkflowSessionModal2}
                handleClose={()=>setWorkflowSessionModal2({open: false, isFirstTime: false})}
                initialValue={username}
                valuesHandler={(value) => {
                    onInputChange("USERNAME", value)
                    setUsername(value)
                }}
            />
            <WorkflowSessionModal
				inputType='password'
				password={true}
                specialPasswordField={true}
                valueLabel={t("LBL_PASSWORD")}
                showAccountData={true}
                showCallingWorkflow={true}
                showDateAndTime={true} 
                showSystemData={true}
                showWorkflowSession={true}
                openModal={workflowSessionModal3} 
                setModalOpen={setWorkflowSessionModal3}
                handleClose={()=>setWorkflowSessionModal3({open: false, isFirstTime: false})}
                initialValue={password}
                valuesHandler={(value) => {
                    onInputChange("PASSWORD", value)
                    setPassword(value)
                }}
            />
		</>
	);
};

export default Database;

export const TestConnection = (props) => {
	const {
		jdbcUrl,
		open,
		close,
		account,
		direction,
		username,
		password,
		type,
		transaction_type,
		db_threads,
	} = props;
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const [message, setMessage] = useState("");

	let body =
		direction === "OUT"
			? {
					DATABASE_TYPE: type,
					PASSWORD: password,
					ADAPTER_TYPE: "DATABASE",
					USERNAME: username,
					JDBCURL: jdbcUrl,
					TRANSACTION_TYPE: transaction_type,
					DB_WRITE_THREADS: db_threads,
			  }
			: {
					DATABASE_TYPE: type,
					PASSWORD: password,
					ADAPTER_TYPE: "DATABASE",
					USERNAME: username,
					JDBCURL: jdbcUrl,
			  };

	const checkConnection = () => {
		dispatch(
			api.endpoints.testPdcConnection.initiate({
				account: account,
				direction: direction,
				body: body,
			})
		).then((res) => {
			if (res?.data?.current_state === "SUCCESS") {
				setMessage(res?.data?.data);
			} else
				setMessage(
					`${t(
						"FRG_COULDNT_CONN_TO_SPECIFIED_DB___MAKE_SURE_ALL_PARAMS_ARE_CORRECT_AND_COM_IS_ABLE_TO_COMM_WITH"
					)} ${jdbcUrl}`
				);
		});
	};

	useEffect(() => {
		setMessage(`${t("LBL_CONNECTING_TO_SERVER")} ${jdbcUrl} as ${username}`);
		if (open) checkConnection();
	}, [open]);

	return (
		<>
			<Dialog
				open={open}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
			>
				<DialogTitle className='dialog-header'>
					<div className='title'>{t("DIAG_TEST_CONNECTION_TIT")}</div>
					<div className='description'>{t("DIAG_TEST_CONNECTION_DESC")}</div>
				</DialogTitle>

				<DialogContent
					className='modal-content'
					dividers
					style={{ whiteSpace: "pre-line" }}
				>
					{message}
				</DialogContent>

				<DialogActions>
					<Button
						size='small'
						onClick={() => close(false)}
						variant={"outlined"}
					>
						{t("BTN_DONE")}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
